import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import '../../themes/edition/EditionContact.css';
import '../../themes/atelier/AtelierContact.css';
import '../../themes/gallery/GalleryContact.css';

const ContactPage = () => {
  const { themeStyles, store, premiumThemeConfig } = useOutletContext() || { themeStyles: {}, store: {} };
  const isEditionTheme = premiumThemeConfig?.theme?.slug === 'edition';
  const isAtelierTheme = premiumThemeConfig?.theme?.slug === 'atelier';
  const isGalleryTheme = premiumThemeConfig?.theme?.slug === 'gallery';

  // Merge premium theme global styles when available
  const globalOverrides = premiumThemeConfig?.globalOverrides || {};
  const premiumColors = premiumThemeConfig?.theme?.globalSettings?.colors || {};
  const premiumFonts = premiumThemeConfig?.theme?.globalSettings?.fonts || {};

  // Color helpers - prefer premium theme colors over basic themeStyles
  const primaryColor = globalOverrides?.colors?.primary || premiumColors?.primary || themeStyles?.primary || '#2563eb';
  const secondaryColor = globalOverrides?.colors?.secondary || premiumColors?.secondary || themeStyles?.secondary || '#1e40af';
  const backgroundColor = globalOverrides?.colors?.background || premiumColors?.background || themeStyles?.background || '#ffffff';
  const borderColor = globalOverrides?.colors?.border || premiumColors?.border || themeStyles?.border || '#e5e7eb';
  const textColor = globalOverrides?.colors?.text || premiumColors?.text || themeStyles?.text || '#111827';
  const mutedColor = globalOverrides?.colors?.muted || premiumColors?.muted || themeStyles?.textMuted || '#6b7280';
  const successColor = themeStyles?.success || '#10b981';
  const errorColor = themeStyles?.error || '#ef4444';
  const borderRadius = themeStyles?.borderRadius || '8px';
  const boxShadow = themeStyles?.shadow || '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
  const headingFont = premiumFonts?.heading || 'inherit';
  const bodyFont = premiumFonts?.body || 'inherit';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Here you would typically send the form data to your backend
      console.log('Form submitted:', formData);
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      title: 'Address',
      content: store?.address || '123 Business Street\nCity, State 12345',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      )
    },
    {
      title: 'Phone',
      content: store?.phone || '+1 (555) 123-4567',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      )
    },
    {
      title: 'Email',
      content: store?.email || 'info@company.com',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      )
    },
    {
      title: 'Hours',
      content: store?.hours || 'Mon - Fri: 9:00 AM - 6:00 PM\nSat: 10:00 AM - 4:00 PM\nSun: Closed',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12,6 12,12 16,14"/>
        </svg>
      )
    }
  ];

  return (
    <>
      <style>{`
        /* Mobile Optimizations for ContactPage */
        @media (max-width: 768px) {
          .contact-hero {
            padding: 3rem 0 !important;
          }
          .contact-hero-title {
            font-size: 2rem !important;
          }
          .contact-hero-subtitle {
            font-size: 1rem !important;
          }
          .contact-section {
            padding: 2rem 0 !important;
          }
          .contact-section-container {
            padding: 0 1rem !important;
          }
          .contact-main-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .contact-form-container,
          .contact-info-container {
            padding: 1.5rem !important;
          }
          .contact-form-title {
            font-size: 1.5rem !important;
          }
          .contact-form-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .contact-submit-button {
            padding: 0.625rem 1.5rem !important;
            font-size: 0.9rem !important;
          }
        }

        @media (max-width: 480px) {
          .contact-hero {
            padding: 2rem 0 !important;
          }
          .contact-hero-title {
            font-size: 1.5rem !important;
          }
          .contact-hero-subtitle {
            font-size: 0.9rem !important;
          }
          .contact-section {
            padding: 1.5rem 0 !important;
          }
          .contact-form-container,
          .contact-info-container {
            padding: 1rem !important;
          }
          .contact-form-title {
            font-size: 1.25rem !important;
          }
        }
      `}</style>
      <div className={isEditionTheme ? 'edition-contact' : isAtelierTheme ? 'atelier-contact' : isGalleryTheme ? 'gallery-contact' : ''}>
        {/* Hero Section */}
        <section className="contact-hero" style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          color: 'white',
          padding: '4rem 0',
          textAlign: 'center'
        }}>
          <div className="contact-section-container" style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 1rem'
          }}>
            <h1 className="contact-hero-title" style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              lineHeight: '1.2'
            }}>
              Contact Us
            </h1>
            <p className="contact-hero-subtitle" style={{
              fontSize: '1.25rem',
              marginBottom: '2rem',
              opacity: 0.9,
              maxWidth: '600px',
              margin: '0 auto 2rem'
            }}>
              Get in touch with us. We'd love to hear from you.
            </p>
          </div>
        </section>

      {/* Contact Form & Info */}
      <section className="contact-section" style={{
        padding: '4rem 0',
        backgroundColor: backgroundColor
      }}>
        <div className="contact-section-container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div className="contact-main-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '3rem'
          }}>
            {/* Contact Form */}
            <div>
              <h2 className="contact-form-title" style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                color: textColor
              }}>
                Send us a Message
              </h2>
              <form onSubmit={handleSubmit} className="contact-form-container" style={{
                backgroundColor: themeStyles?.background || 'white',
                padding: '2rem',
                borderRadius: borderRadius,
                boxShadow: boxShadow
              }}>
                <div className="contact-form-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: textColor
                    }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${borderColor}`,
                        borderRadius: borderRadius,
                        fontSize: '1rem',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = borderColor;
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: textColor
                    }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${borderColor}`,
                        borderRadius: borderRadius,
                        fontSize: '1rem',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = borderColor;
                      }}
                    />
                  </div>
                </div>

                <div className="contact-form-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: textColor
                    }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${borderColor}`,
                        borderRadius: borderRadius,
                        fontSize: '1rem',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = borderColor;
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: textColor
                    }}>
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `1px solid ${borderColor}`,
                        borderRadius: borderRadius,
                        fontSize: '1rem',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = primaryColor;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = borderColor;
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: textColor
                  }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `1px solid ${borderColor}`,
                      borderRadius: borderRadius,
                      fontSize: '1rem',
                      resize: 'vertical',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = primaryColor;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = borderColor;
                    }}
                  />
                </div>

                {submitStatus === 'success' && (
                  <div style={{
                    backgroundColor: '#d1fae5',
                    color: '#065f46',
                    padding: '0.75rem',
                    borderRadius: borderRadius,
                    marginBottom: '1rem',
                    border: '1px solid #a7f3d0'
                  }}>
                    Thank you for your message! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div style={{
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    padding: '0.75rem',
                    borderRadius: borderRadius,
                    marginBottom: '1rem',
                    border: '1px solid #fca5a5'
                  }}>
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="contact-submit-button"
                  style={{
                    backgroundColor: isSubmitting ? secondaryColor : primaryColor,
                    color: 'white',
                    padding: '0.75rem 2rem',
                    border: 'none',
                    borderRadius: borderRadius,
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    minHeight: '44px'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = secondaryColor;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = primaryColor;
                    }
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="contact-form-title" style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                color: textColor
              }}>
                Get in Touch
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem'
              }}>
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1.5rem',
                      backgroundColor: themeStyles?.background || 'white',
                      borderRadius: borderRadius,
                      boxShadow: boxShadow,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = boxShadow;
                    }}
                  >
                    <div style={{
                      color: primaryColor,
                      marginTop: '0.25rem',
                      flexShrink: 0
                    }}>
                      {info.icon}
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        color: textColor
                      }}>
                        {info.title}
                      </h3>
                      <p style={{
                        color: mutedColor,
                        lineHeight: '1.6',
                        whiteSpace: 'pre-line'
                      }}>
                        {info.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="contact-section" style={{
        padding: '4rem 0',
        backgroundColor: borderColor
      }}>
        <div className="contact-section-container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <h2 className="contact-form-title" style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: '2rem',
            color: textColor
          }}>
            Find Us
          </h2>
          <div style={{
            height: '400px',
            backgroundColor: borderColor,
            borderRadius: borderRadius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: mutedColor
          }}>
            {/* Map placeholder - you can integrate with Google Maps or other map service */}
            <div style={{ textAlign: 'center' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginBottom: '1rem' }}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <p>Interactive Map</p>
              <p style={{ fontSize: '0.875rem' }}>Map integration coming soon</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ContactPage;
